import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Watch, CheckCircle2, X, Footprints, Flame, Heart, Moon, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * FitbitConnect — wearable integration tile shown on /profile.
 *
 * Flow:
 *   1. GET /fitbit/status — tells us whether the backend is configured and
 *      whether THIS user has connected their account.
 *   2. If not connected, "Connect Fitbit" → GET /fitbit/authorize → backend
 *      returns `{authorize_url}`. We window.location.href = authorize_url
 *      so the browser navigates to fitbit.com. After approval Fitbit bounces
 *      to /api/fitbit/callback which 302s back to /profile?fitbit=connected.
 *   3. If connected, render Today's snapshot (steps / calories / HR / sleep)
 *      with a Refresh button that hits /fitbit/today.
 *
 * "configured: false" means the backend admin (you) hasn't set
 * FITBIT_CLIENT_ID etc. in backend/.env yet. We surface a friendly setup
 * note so the tile never silently breaks.
 */
export default function FitbitConnect() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState({ configured: null, connected: false });
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/fitbit/status");
      setStatus(data);
      if (data.connected) {
        try {
          const t = await api.get("/fitbit/today");
          setToday(t.data);
        } catch {
          // Tokens may have just expired/been revoked — surface the error
          // but don't blow up the tile; user can hit Refresh to retry.
          setToday(null);
        }
      } else {
        setToday(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Surface the OAuth round-trip outcome from the URL query string Fitbit
  // sends us back to. Clear the params after we read them so a page refresh
  // doesn't re-toast the user.
  useEffect(() => {
    const flag = searchParams.get("fitbit");
    if (!flag) return;
    if (flag === "connected") {
      toast.success("Fitbit connected 🎉");
      loadStatus();
    } else if (flag === "error") {
      const reason = searchParams.get("reason") || "unknown";
      toast.error(`Fitbit connection failed: ${reason}`);
    }
    searchParams.delete("fitbit");
    searchParams.delete("reason");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, loadStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/fitbit/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not start Fitbit OAuth";
      toast.error(detail);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Fitbit account?")) return;
    setBusy(true);
    try {
      await api.post("/fitbit/disconnect");
      toast.success("Fitbit disconnected");
      setToday(null);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't disconnect");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/fitbit/today");
      setToday(data);
      toast.success("Refreshed from Fitbit");
    } catch (e) {
      const status_code = e.response?.status;
      if (status_code === 401) {
        toast.error("Fitbit session expired — please reconnect.");
        await loadStatus();
      } else {
        toast.error(e.response?.data?.detail || "Couldn't refresh");
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="fitbit-tile-loading" className="border border-neutral-800 p-5 mb-5 flex items-center gap-3 text-sm text-neutral-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking Fitbit…
      </div>
    );
  }

  // Backend not configured yet — show admin a setup note.
  if (status.configured === false) {
    return (
      <div data-testid="fitbit-tile-unconfigured" className="border border-neutral-800 p-5 mb-5">
        <div className="flex items-center gap-3 mb-2">
          <Watch className="w-5 h-5 text-neutral-500" />
          <h2 className="font-display text-xl uppercase">Wearable Sync (Fitbit)</h2>
        </div>
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs mt-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Not yet enabled</div>
            <div className="text-neutral-300">
              The backend admin must register a free Fitbit developer app at <span className="text-blue-500">dev.fitbit.com</span> and add the credentials to <span className="font-mono-d">backend/.env</span>. See <span className="font-mono-d">routes/fitbit.py</span> for the setup checklist.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Configured but not connected
  if (!status.connected) {
    return (
      <div data-testid="fitbit-tile-disconnected" className="border border-neutral-800 p-5 mb-5">
        <div className="flex items-center gap-3 mb-2">
          <Watch className="w-5 h-5 text-blue-500" />
          <h2 className="font-display text-xl uppercase">Wearable Sync</h2>
        </div>
        <p className="text-xs text-neutral-400 mb-3">
          Connect your <span className="text-blue-500 font-semibold">Fitbit</span> account to auto-sync steps, heart rate, sleep, and active minutes into your dashboard.
        </p>
        <Button
          data-testid="fitbit-connect-btn"
          onClick={connect}
          disabled={busy}
          className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Watch className="w-4 h-4 mr-2" />}
          Connect Fitbit
        </Button>
      </div>
    );
  }

  // Connected — show today's snapshot
  const summary = today?.summary || {};
  const heart = today?.heart || {};
  const sleep = today?.sleep || {};
  const steps = summary.steps ?? "—";
  const calories = summary.caloriesOut ?? "—";
  const activeMin = (summary.fairlyActiveMinutes || 0) + (summary.veryActiveMinutes || 0);
  const restingHR = heart.value?.restingHeartRate ?? "—";
  const sleepHours = sleep.duration ? (sleep.duration / 3_600_000).toFixed(1) : "—";

  return (
    <div data-testid="fitbit-tile-connected" className="border border-neutral-800 p-5 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Watch className="w-5 h-5 text-blue-500" />
          <h2 className="font-display text-xl uppercase">Wearable Sync</h2>
          <span className="inline-flex items-center text-[10px] uppercase tracking-widest text-green-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Fitbit</span>
        </div>
        <div className="flex gap-1.5">
          <Button
            size="icon"
            variant="ghost"
            data-testid="fitbit-refresh-btn"
            onClick={refresh}
            disabled={busy}
            title="Refresh from Fitbit"
            className="text-neutral-400 hover:text-blue-500 h-8 w-8"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            data-testid="fitbit-disconnect-btn"
            onClick={disconnect}
            disabled={busy}
            title="Disconnect Fitbit"
            className="text-neutral-400 hover:text-blue-500 h-8 w-8"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard testid="fitbit-stat-steps" icon={Footprints} label="Steps" value={typeof steps === "number" ? steps.toLocaleString() : steps} />
        <StatCard testid="fitbit-stat-calories" icon={Flame} label="Calories" value={typeof calories === "number" ? calories.toLocaleString() : calories} />
        <StatCard testid="fitbit-stat-hr" icon={Heart} label="Resting HR" value={restingHR} unit={typeof restingHR === "number" ? "bpm" : ""} />
        <StatCard testid="fitbit-stat-sleep" icon={Moon} label="Sleep" value={sleepHours} unit={sleepHours !== "—" ? "h" : ""} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">
        Active: <span className="text-neutral-300">{activeMin} min today</span>
      </div>
    </div>
  );
}

function StatCard({ testid, icon: Icon, label, value, unit = "" }) {
  return (
    <div data-testid={testid} className="border border-neutral-800 p-2.5 bg-neutral-900/50">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display text-xl mt-0.5">{value}{unit && <span className="text-[10px] text-neutral-500 ml-1">{unit}</span>}</div>
    </div>
  );
}
